import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ProteinAtlasGeneralInfoTable from "./ProteinAtlasGeneralInfoTable";
import ProteinAtlasPathogenicityHistogram from "./ProteinAtlasPathogenicityHistogram";
import { toTitleCase, getLevelColor, getRiskLevel } from "./helpers";
import Limiter from "../../../../components/atoms/Limiter";
import ExpandLink from "../../../../components/atoms/Limiter/ExpandLink";
import TableSorter from "../../../../components/atoms/TableSorter";
import ProteinAtlasTissueInfoTable from "./ProteinAtlasTissueInfoTable";
import ProteinAtlasTissueHistogram from "./ProteinAtlasTissueHistogram";

const sortByCell = (a, b) => {
  const x = a.cell_type;
  const y = b.cell_type;
  if (x === "" || x === null) return 1;
  if (y === "" || y === null) return -1;
  if (x === y) return 0;
  return x < y ? -1 : 1;
};

const renderTable = (props) => {
  return <ProteinAtlasTissueInfoTable {...props} />;
};

const renderLimiter = (props) => {
  return (
    <div className="varsome-inner-container">
      <Limiter
        {...props}
        ExpandLink={(p) => (
          <div className="tw-p-2">
            <ExpandLink {...p} />
          </div>
        )}
        items={props.sortedItems}
        limit={10}
        renderContent={renderTable}
      />
    </div>
  );
};

function ProteinAtlasContent(props) {
  const { proteinAtlasData } = props;
  const {
    general_info: generalInfoData,
    pathology_info: pathologyInfoData,
    tissue_info: tissueInfoData,
  } = proteinAtlasData;
  const [gData, setGeneralData] = useState([]);
  const [pData, setPathologyData] = useState([]);
  const [tData, setTissueData] = useState([]);
  const [tHData, setHistogramTissueData] = useState([]);

  useEffect(() => {
    const generalData = [];
    Object.entries(generalInfoData).forEach(([key, value]) => {
      if (value) {
        const transformedKey = key.replaceAll("_", " ");
        const capitalizedTransformedKey =
          transformedKey &&
          transformedKey[0].toUpperCase() + transformedKey.slice(1);
        const capitalizedValue =
          value && value[0].toUpperCase() + value.slice(1);
        generalData.push({
          key: capitalizedTransformedKey,
          value: capitalizedValue,
        });
      }
    });
    setGeneralData(generalData);
  }, [generalInfoData]);

  useEffect(() => {
    const pathologyData = pathologyInfoData.map((x, i) => {
      const total =
        Number(x.high_risk) +
        Number(x.low_risk) +
        Number(x.medium_risk) +
        Number(x.not_detected);
      const totalHighMedium = Number(x.high_risk) + Number(x.medium_risk);
      const value = total !== 0 ? (totalHighMedium / total) * 100 : 0;
      return {
        ...x,
        total,
        totalHighMedium,
        value,
        id: `${i}${new Date()}`,
      };
    });
    const y = pathologyData.sort((a, b) => b.value - a.value);
    setPathologyData(y);
  }, [pathologyInfoData]);

  useEffect(() => {
    const tissueData =
      tissueInfoData &&
      tissueInfoData.map((x, i) => {
        const capitalizedTissue = x.tissue_type && toTitleCase(x.tissue_type);
        const levelColor = x.level && getLevelColor(x.level);
        return {
          ...x,
          tissue_type: capitalizedTissue,
          levelColor,
          id: `${i}${new Date()}`,
        };
      });
    setTissueData(tissueData);
  }, [tissueInfoData]);

  useEffect(() => {
    const tissueData = [];
    const sortByRisk = ["Not Detected", "Low", "Medium", "High"];
    if (tissueInfoData) {
      tissueInfoData.forEach((x) => {
        const found = tissueData.find((el) => el.tissue_type === x.tissue_type);
        if (!found) {
          const obj = {
            tissue_type: x.tissue_type,
            cells: [
              {
                cell_type: x.cell_type,
                risk: x.level,
                reliability: x.reliability,
              },
            ],
          };
          tissueData.push(obj);
        }
        if (found) {
          const cellObj = {
            cell_type: x.cell_type,
            risk: x.level,
            reliability: x.reliability,
          };
          found.cells.push(cellObj);
        }
      });
    }

    const issueHistogramData = tissueData.map((x) => {
      const capitalizedTissue = x.tissue_type && toTitleCase(x.tissue_type);
      const level = getRiskLevel(x.cells);
      const sortedCells = x.cells.sort(
        (a, b) => sortByRisk.indexOf(b.risk) - sortByRisk.indexOf(a.risk)
      );

      return {
        ...x,
        tissue_type: capitalizedTissue,
        level,
        cells: sortedCells,
      };
    });

    const sortedTissueHistogramData = issueHistogramData.sort(
      (a, b) => sortByRisk.indexOf(b.level) - sortByRisk.indexOf(a.level)
    );

    setHistogramTissueData(sortedTissueHistogramData);
  }, [tissueInfoData]);

  return (
    <div className="varsome-inner-container">
      <ProteinAtlasGeneralInfoTable items={gData} />
      {pData && <ProteinAtlasPathogenicityHistogram data={pData} />}
      {tHData && tHData.length !== 0 && (
        <ProteinAtlasTissueHistogram data={tHData} />
      )}
      {tData && (
        <TableSorter
          {...props}
          items={tData}
          defaultSortedField="cell_type"
          defaultComparator={sortByCell}
          renderContent={renderLimiter}
        />
      )}
    </div>
  );
}

ProteinAtlasContent.propTypes = {
  proteinAtlasData: PropTypes.shape({
    version: PropTypes.string,
    general_info: PropTypes.shape({
      biological_process: PropTypes.string,
      cancer_type: PropTypes.arrayOf(PropTypes.string),
      disease_involvement: PropTypes.string,
      hpa_evidence: PropTypes.string,
      molecular_function: PropTypes.string,
      normalized_disease: PropTypes.arrayOf(PropTypes.string),
      protein_class: PropTypes.string,
      rna_cancer_distribution: PropTypes.string,
      rna_cancer_specificity: PropTypes.string,
      rna_cancer_specificity_score: PropTypes.string,
      rna_specific_fpkm: PropTypes.string,
      uniprot_id: PropTypes.string,
    }),
    pathology_info: PropTypes.arrayOf(
      PropTypes.shape({
        cancer_name: PropTypes.string,
        high_risk: PropTypes.string,
        low_risk: PropTypes.string,
        medium_risk: PropTypes.string,
        not_detected: PropTypes.string,
        normalized_disease: PropTypes.string,
      })
    ),
    tissue_info: PropTypes.arrayOf(
      PropTypes.shape({
        cell_type: PropTypes.string,
        level: PropTypes.string,
        reliability: PropTypes.string,
        tissue_type: PropTypes.string,
      })
    ),
  }),
};

ProteinAtlasContent.defaultProps = {
  proteinAtlasData: null,
};

export default ProteinAtlasContent;
